import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { X, ArrowRight, ArrowLeft, Check, LayoutDashboard, ChevronDown, ChevronUp, Eye, EyeOff, MapPin } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { useNavigate, useLocation } from 'react-router';
import { useAuth, type UserRole } from '../context/AuthContext';
import { getVisibleRoutes, getVisibleCategories } from '../config/navigation';
import { savePreferenceSettings } from '../services/api';

// ========================
// TYPES
// ========================

interface TutorialTip {
  icon: string;
  text: string;
  type: 'info' | 'warning' | 'success' | 'shortcut';
}

interface OnboardingStep {
  id: string;
  title: string;
  description: string;
  category: string;
  categoryIcon: string;
  /** The navigable route - routes with :params use a real example fallback */
  route?: string;
  /** Actual navigable path (for parameterized routes) */
  navigateTo?: string;
  tips?: TutorialTip[];
  illustration: string;
  /** Highlights to look for on the page */
  lookFor?: string;
  /** Which roles see this step. undefined = all roles */
  roles?: UserRole[];
  /** Per-role description overrides */
  roleDescriptions?: Partial<Record<UserRole, string>>;
  /** Per-role title overrides */
  roleTitles?: Partial<Record<UserRole, string>>;
  /** Per-role navigateTo overrides */
  roleNavigateTo?: Partial<Record<UserRole, string>>;
  /** Per-role lookFor overrides */
  roleLookFor?: Partial<Record<UserRole, string>>;
}

// ========================
// CATEGORIES
// ========================

// NOTE: Tutorial step visibility is derived from /src/app/config/navigation.ts
// via getVisibleRoutes(). No hardcoded role arrays needed here — the navigation
// config is the single source of truth for what each role can see.
// The `roles` field on steps is ONLY used for steps without a route/navigateTo
// (e.g. welcome, conclusion). For all route-based steps, visibility comes from
// the navigation config automatically.

// ========================
// COMPLETE STEPS DATA
// ========================

const allSteps: OnboardingStep[] = [
  // ============================================================
  // INICIO / WELCOME (all roles)
  // ============================================================
  {
    id: 'welcome',
    title: 'Bem-vindo ao SimulaDesk!',
    description: 'A plataforma completa de treinamento e auditoria de atendimento com IA. Vamos navegar por cada tela para voce conhecer tudo na pratica.',
    category: 'inicio',
    categoryIcon: '🏠',
    navigateTo: '/',
    illustration: '👋',
    roleTitles: {
      operador: 'Bem-vindo ao SimulaDesk!',
      superadmin: 'Bem-vindo, Super Admin!',
    },
    roleDescriptions: {
      operador: 'Sua plataforma de treinamento e desenvolvimento profissional. Vamos conhecer as telas disponiveis para voce acompanhar seu desempenho e evoluir.',
      superadmin: 'Painel de gestao multi-tenant do SimulaDesk. Vamos conhecer as ferramentas de administracao de empresas, usuarios e configuracoes.',
    },
    roleNavigateTo: {
      superadmin: '/superadmin/dashboard',
    },
    tips: [
      { icon: '⌨️', text: 'Use as setas ← → do teclado para navegar entre os passos', type: 'shortcut' },
      { icon: '👁️', text: 'A cada passo, a tela real sera exibida atras do tutorial', type: 'info' },
      { icon: '💡', text: 'Voce pode rever este tutorial em Configuracoes → Reiniciar Tutorial', type: 'info' },
    ],
  },

  // ============================================================
  // SUPER ADMIN group (superadmin only)
  // ============================================================
  {
    id: 'superadmin-dashboard',
    title: 'Visao Geral Multi-Tenant',
    description: 'Dashboard macro com KPIs de volume: total de empresas, auditorias, operadores e licencas. Graficos de evolucao, distribuicao por plano e auditorias por empresa.',
    category: 'super_admin',
    categoryIcon: '🌐',
    route: '/superadmin/dashboard',
    navigateTo: '/superadmin/dashboard',
    illustration: '🌐',
    lookFor: 'Veja os KPIs de volume, graficos de evolucao e pontos de atencao',
    roles: ['superadmin'],
    tips: [
      { icon: '📊', text: 'Area chart mostra evolucao mensal de todas as empresas', type: 'info' },
      { icon: '⚠️', text: 'O card de Pontos de Atencao destaca empresas com metricas criticas', type: 'warning' },
    ],
  },
  {
    id: 'tenant-management',
    title: 'Gestao de Empresas',
    description: 'CRUD completo de empresas (tenants). Veja planos (starter/professional/enterprise), toggle de features, filtros por status e cards expandiveis com detalhes.',
    category: 'super_admin',
    categoryIcon: '🌐',
    route: '/superadmin/empresas',
    navigateTo: '/superadmin/empresas',
    illustration: '🏢',
    lookFor: 'Veja os cards de empresas com plano, status e features ativas',
    roles: ['superadmin'],
    tips: [
      { icon: '💡', text: 'Clique no card para expandir e ver detalhes da empresa', type: 'info' },
      { icon: '⚙️', text: 'Toggle de features permite ativar/desativar modulos por empresa', type: 'info' },
    ],
  },

  // ============================================================
  // PRINCIPAL group (admin, qualidade, supervisor)
  // ============================================================
  {
    id: 'dashboard',
    title: 'Dashboard Principal',
    description: 'Sua visao geral com KPIs em tempo real: media de auditoria, media de treino, total de sessoes, taxa de falha grave e progresso das metas.',
    category: 'inicio',
    categoryIcon: '🏠',
    route: '/',
    navigateTo: '/',
    illustration: '📊',
    roles: ['admin', 'qualidade', 'supervisor'],
    lookFor: 'Observe os cards de KPI no topo e o grafico de desempenho abaixo',
    tips: [
      { icon: '💡', text: 'Os widgets mostram dados atualizados automaticamente', type: 'info' },
    ],
  },
  {
    id: 'dashboard-operador',
    title: 'Meu Dashboard',
    description: 'Sua visao pessoal com seus KPIs: score medio, treinos realizados, auditorias recebidas e alertas de falha grave. Tudo sobre seu desempenho.',
    category: 'inicio',
    categoryIcon: '🏠',
    route: '/',
    navigateTo: '/',
    illustration: '📊',
    roles: ['operador'],
    lookFor: 'Veja seus KPIs pessoais, proximos passos e alertas no topo',
    tips: [
      { icon: '💡', text: 'Os widgets mostram dados atualizados automaticamente', type: 'info' },
    ],
  },
  {
    id: 'operator-profile',
    title: 'Meu Perfil',
    description: 'Seu perfil completo com 6 abas: Visao Geral, Auditorias, Treinos, Badges, Metas e Evolucao. Inclui radar chart de habilidades e 8 badges possiveis.',
    category: 'inicio',
    categoryIcon: '🏠',
    route: '/meu-perfil',
    navigateTo: '/meu-perfil',
    illustration: '👤',
    lookFor: 'Explore as 6 abas e o radar chart de habilidades',
    roles: ['operador'],
    tips: [
      { icon: '🏅', text: 'Desbloqueie badges completando desafios especificos', type: 'success' },
      { icon: '📊', text: 'O radar chart mostra suas 5 dimensoes de habilidade', type: 'info' },
    ],
  },
  {
    id: 'custom-dashboard',
    title: 'Dashboard Personalizado',
    description: 'Arraste e reorganize widgets, escolha quais metricas visualizar e crie sua propria tela de monitoramento.',
    category: 'inicio',
    categoryIcon: '🏠',
    route: '/dashboard-personalizado',
    navigateTo: '/dashboard-personalizado',
    illustration: '🧩',
    lookFor: 'Veja os widgets que voce pode arrastar e reorganizar',
    roles: ['admin', 'qualidade', 'supervisor'],
    tips: [
      { icon: '💡', text: 'Arraste os widgets para reorganizar sua posicao', type: 'info' },
    ],
  },
  {
    id: 'notifications',
    title: 'Central de Notificacoes',
    description: 'Todas as alertas em um so lugar: auditorias publicadas, badges conquistadas, metas atingidas, feedbacks e avisos do sistema.',
    category: 'inicio',
    categoryIcon: '🏠',
    route: '/notificacoes',
    navigateTo: '/notificacoes',
    illustration: '🔔',
    lookFor: 'Veja a lista de notificacoes com filtros por tipo e prioridade',
    roles: ['admin', 'qualidade', 'supervisor'],
    tips: [
      { icon: '⚡', text: 'Notificacoes de alta prioridade aparecem em destaque', type: 'warning' },
      { icon: '💡', text: 'Marque todas como lidas com um clique', type: 'info' },
    ],
  },
  {
    id: 'notifications-operador',
    title: 'Minhas Notificacoes',
    description: 'Suas notificacoes: auditorias publicadas, badges conquistadas, metas atingidas e feedbacks recebidos.',
    category: 'inicio',
    categoryIcon: '🏠',
    route: '/notificacoes',
    navigateTo: '/notificacoes',
    illustration: '🔔',
    lookFor: 'Veja a lista de notificacoes com filtros por tipo e prioridade',
    roles: ['operador'],
    tips: [
      { icon: '⚡', text: 'Notificacoes de alta prioridade aparecem em destaque', type: 'warning' },
    ],
  },
  {
    id: 'evolution',
    title: 'Historico de Evolucao',
    description: 'Timeline completa: cada auditoria, treino, badge, meta e certificado. Filtre por periodo (7d, 30d, 90d) e tipo de evento.',
    category: 'inicio',
    categoryIcon: '🏠',
    route: '/historico-evolucao',
    navigateTo: '/historico-evolucao',
    illustration: '📜',
    lookFor: 'Veja a timeline vertical com eventos e comparativos de score',
    roles: ['admin', 'qualidade', 'supervisor'],
    tips: [
      { icon: '📊', text: 'Eventos mostram comparativo com score anterior', type: 'info' },
    ],
  },
  {
    id: 'evolution-operador',
    title: 'Minha Evolucao',
    description: 'Sua timeline completa de evolucao: auditorias, treinos, badges e metas. Acompanhe como voce progrediu ao longo do tempo.',
    category: 'inicio',
    categoryIcon: '🏠',
    route: '/historico-evolucao',
    navigateTo: '/historico-evolucao',
    illustration: '📜',
    lookFor: 'Veja sua timeline pessoal de eventos e evolucao de score',
    roles: ['operador'],
    tips: [
      { icon: '📊', text: 'Eventos mostram comparativo com score anterior', type: 'info' },
    ],
  },

  // ============================================================
  // QUALIDADE group (admin, qualidade)
  // ============================================================
  {
    id: 'quality-dashboard',
    title: 'Dashboard Qualidade',
    description: 'Visao consolidada da area de qualidade: auditorias pendentes, scores medios, comparativo IA vs Humano e metricas de conformidade.',
    category: 'qualidade',
    categoryIcon: '🛡️',
    route: '/qualidade',
    navigateTo: '/qualidade',
    illustration: '🛡️',
    lookFor: 'Veja os KPIs de qualidade e a fila de pendencias',
    roles: ['admin', 'qualidade'],
    tips: [
      { icon: '🤖', text: 'Compare avaliacoes de monitores humanos vs IA', type: 'info' },
    ],
  },
  {
    id: 'audit-queue',
    title: 'Fila de Auditorias',
    description: 'Gerencie a fila de auditorias pendentes de revisao. Priorize por urgencia, atribua monitores e acompanhe SLAs de resposta.',
    category: 'qualidade',
    categoryIcon: '🛡️',
    route: '/qualidade/fila-auditorias',
    navigateTo: '/qualidade/fila-auditorias',
    illustration: '▶️',
    lookFor: 'Veja a fila priorizada com status e atribuicoes',
    roles: ['admin', 'qualidade'],
    tips: [
      { icon: '⏱️', text: 'Auditorias com SLA proximo do vencimento ficam em destaque', type: 'warning' },
    ],
  },
  {
    id: 'upload-audit',
    title: 'Upload de Auditoria',
    description: 'Envie novos audios para avaliacao. Selecione operador, supervisor, setor, monitor (humano ou IA) e questionario.',
    category: 'qualidade',
    categoryIcon: '🛡️',
    route: '/upload-auditoria',
    navigateTo: '/upload-auditoria',
    illustration: '📤',
    lookFor: 'Veja o formulario de upload com os campos obrigatorios',
    roles: ['admin', 'qualidade'],
    tips: [
      { icon: '🤖', text: 'Monitores IA processam o audio e geram avaliacao automatica', type: 'success' },
      { icon: '💡', text: 'Formatos aceitos: MP3, WAV, OGG', type: 'info' },
    ],
  },
  {
    id: 'monitor-management',
    title: 'Gestao de Monitores',
    description: 'Gerencie os usuarios monitores de qualidade: atribua auditorias, veja metricas de produtividade e configure permissoes de avaliacao.',
    category: 'qualidade',
    categoryIcon: '🛡️',
    route: '/admin/monitores',
    navigateTo: '/admin/monitores',
    illustration: '👥',
    lookFor: 'Veja a lista de monitores com metricas e atribuicoes',
    roles: ['admin', 'qualidade'],
    tips: [
      { icon: '💡', text: 'Monitores sao usuarios com funcao de avaliacao de qualidade', type: 'info' },
      { icon: '🤖', text: 'Monitores IA sao entidades automaticas, nao usuarios', type: 'info' },
    ],
  },

  // ============================================================
  // MONITORAMENTO group (admin, qualidade, supervisor + operador)
  // ============================================================
  {
    id: 'audits',
    title: 'Auditorias',
    description: 'Lista completa de todas as auditorias. Filtre por operador, supervisor, setor, monitor, status e periodo. Veja score, falhas graves e transcricoes.',
    category: 'monitoramento',
    categoryIcon: '📊',
    route: '/auditorias',
    navigateTo: '/auditorias',
    illustration: '📁',
    lookFor: 'Observe a tabela com todas as auditorias e seus status',
    roles: ['admin', 'qualidade', 'supervisor'],
    tips: [
      { icon: '⚠️', text: 'Auditorias com Falha Grave exibem score ZERO ("Falha Grave Zera")', type: 'warning' },
      { icon: '💡', text: 'Clique em qualquer auditoria para ver detalhes completos', type: 'info' },
    ],
  },
  {
    id: 'audits-operador',
    title: 'Minhas Auditorias',
    description: 'Todas as auditorias realizadas sobre seus atendimentos. Veja seu score, itens avaliados e feedback de cada monitoria.',
    category: 'monitoramento',
    categoryIcon: '📊',
    route: '/auditorias',
    navigateTo: '/auditorias',
    illustration: '📁',
    lookFor: 'Veja a lista das suas auditorias com scores e status',
    roles: ['operador'],
    tips: [
      { icon: '⚠️', text: 'Auditorias com Falha Grave exibem score ZERO ("Falha Grave Zera")', type: 'warning' },
      { icon: '💡', text: 'Clique em qualquer auditoria para ver detalhes completos', type: 'info' },
    ],
  },
  {
    id: 'audit-detail',
    title: 'Detalhe da Auditoria',
    description: 'Avaliacao item por item do scorecard, transcricao com timestamps, player de audio, resumo da IA e comentarios do auditor.',
    category: 'monitoramento',
    categoryIcon: '📊',
    route: '/auditorias/:id',
    navigateTo: '/auditorias',
    illustration: '🔍',
    lookFor: 'Veja o scorecard detalhado e a transcricao do atendimento',
    roleDescriptions: {
      operador: 'Detalhes da sua auditoria: scorecard item por item, transcricao do atendimento, resumo e areas de melhoria identificadas.',
    },
    tips: [
      { icon: '🎧', text: 'A transcricao acompanha o audio em tempo real', type: 'info' },
      { icon: '🤖', text: 'O resumo e gerado automaticamente pela IA', type: 'info' },
    ],
  },
  {
    id: 'questionnaires',
    title: 'Questionarios',
    description: 'Crie e gerencie questionarios de autoavaliacao, satisfacao e desempenho. Tipos: texto, multipla escolha, rating, sim/nao e escala.',
    category: 'monitoramento',
    categoryIcon: '📊',
    route: '/questionarios',
    navigateTo: '/questionarios',
    illustration: '📝',
    lookFor: 'Veja os questionarios disponiveis e suas respostas',
    roles: ['admin', 'qualidade', 'supervisor'],
    tips: [
      { icon: '💡', text: 'Questionarios podem ser direcionados para operadores, supervisores ou todos', type: 'info' },
      { icon: '📊', text: 'Perguntas com peso geram score automatico', type: 'info' },
    ],
  },
  {
    id: 'analytics',
    title: 'Analytics Avancado',
    description: 'Graficos de performance: evolucao temporal, distribuicao de scores, comparativo IA vs Humano, taxa de falha grave e predicoes.',
    category: 'monitoramento',
    categoryIcon: '📊',
    route: '/analitica',
    navigateTo: '/analitica',
    illustration: '📈',
    lookFor: 'Explore os graficos interativos e a secao IA vs Humano',
    roles: ['admin', 'qualidade', 'supervisor'],
    tips: [
      { icon: '🤖', text: 'A secao "IA vs Humano" compara avaliacoes por tipo de monitor', type: 'info' },
      { icon: '📊', text: 'Passe o mouse sobre os graficos para ver detalhes', type: 'shortcut' },
    ],
  },
  {
    id: 'reports',
    title: 'Relatorios',
    description: 'Relatorios completos por operador, equipe ou setor. Exporte em PDF ou Excel com graficos e recomendacoes.',
    category: 'monitoramento',
    categoryIcon: '📊',
    route: '/relatorios',
    navigateTo: '/relatorios',
    illustration: '📄',
    lookFor: 'Veja as opcoes de geracao de relatorio e exportacao',
    roles: ['admin', 'qualidade', 'supervisor'],
    tips: [
      { icon: '📥', text: 'Exporte em PDF (jsPDF) ou Excel (xlsx)', type: 'shortcut' },
    ],
  },

  // ============================================================
  // TREINAMENTO group (admin, qualidade, supervisor + operador)
  // ============================================================
  {
    id: 'training',
    title: 'Modulo de Treino',
    description: 'Escolha entre 6 cenarios: Reclamacao, Suporte Tecnico, Retencao, Financeiro, Troca/Devolucao e Vendas. Cada um com perfil de cliente, objetivos e dicas.',
    category: 'treinamento',
    categoryIcon: '🎯',
    route: '/treino',
    navigateTo: '/treino',
    illustration: '🎯',
    lookFor: 'Veja os cards de cenarios com dificuldade e categoria',
    roles: ['admin', 'qualidade', 'supervisor'],
    tips: [
      { icon: '💡', text: 'Comece pelos cenarios "Facil" e avance gradualmente', type: 'info' },
      { icon: '🤖', text: 'A IA simula o cliente com diferentes humores', type: 'info' },
      { icon: '⚠️', text: 'Falhas Graves zeram a nota!', type: 'warning' },
    ],
  },
  {
    id: 'operator-training-hub',
    title: 'Centro de Treinamento',
    description: 'Sua jornada de treinamento com 4 fases de simulacao. Pratique cenarios reais de atendimento com IA e evolua progressivamente.',
    category: 'treinamento',
    categoryIcon: '🎯',
    route: '/centro-treinamento',
    navigateTo: '/centro-treinamento',
    illustration: '🎓',
    lookFor: 'Veja as 4 fases da jornada e seu progresso em cada uma',
    roles: ['operador'],
    tips: [
      { icon: '🤖', text: 'A IA simula clientes com diferentes humores e cenarios', type: 'info' },
      { icon: '💡', text: 'Complete uma fase para desbloquear a proxima', type: 'info' },
    ],
  },
  {
    id: 'training-calendar',
    title: 'Calendario de Treinos',
    description: 'Planeje e agende sessoes de treinamento no calendario visual. Veja treinos passados com scores e agende novos.',
    category: 'treinamento',
    categoryIcon: '🎯',
    route: '/calendario-treinos',
    navigateTo: '/calendario-treinos',
    illustration: '📅',
    lookFor: 'Veja o calendario com eventos de treino marcados',
    roleDescriptions: {
      operador: 'Seu calendario de treinos: veja sessoes passadas com scores e agende novos treinos para continuar evoluindo.',
    },
    tips: [
      { icon: '💡', text: 'Clique em uma data para agendar novo treino', type: 'info' },
      { icon: '🏆', text: 'Treinos concluidos mostram o score obtido', type: 'success' },
    ],
  },
  {
    id: 'scripts',
    title: 'Biblioteca de Scripts',
    description: 'Scripts prontos organizados por categoria: abertura, objecao, fechamento, saudacao, despedida e solucao. Copie, favorite e crie os seus.',
    category: 'treinamento',
    categoryIcon: '🎯',
    route: '/biblioteca-scripts',
    navigateTo: '/biblioteca-scripts',
    illustration: '📚',
    lookFor: 'Veja os scripts organizados por categoria com botao de copiar',
    roles: ['admin', 'qualidade', 'supervisor'],
    tips: [
      { icon: '📋', text: 'Clique no icone de copiar para usar o script', type: 'shortcut' },
      { icon: '⭐', text: 'Favorite scripts para acesso rapido', type: 'info' },
    ],
  },
  {
    id: 'feedback',
    title: 'Feedback 360',
    description: 'De e receba feedback: elogio, sugestao, critica construtiva e reconhecimento. Pode ser anonimo. Curta feedbacks publicos.',
    category: 'treinamento',
    categoryIcon: '🎯',
    route: '/feedback-360',
    navigateTo: '/feedback-360',
    illustration: '💬',
    lookFor: 'Veja os feedbacks recebidos e o formulario para enviar novos',
    roles: ['admin', 'qualidade', 'supervisor'],
    tips: [
      { icon: '🎭', text: 'Feedbacks anonimos protegem a identidade do autor', type: 'info' },
      { icon: '❤️', text: 'Curta feedbacks para incentivar a cultura de reconhecimento', type: 'success' },
    ],
  },
  {
    id: 'feedback-operador',
    title: 'Feedback 360',
    description: 'Receba e envie feedbacks: elogios, sugestoes e reconhecimentos. Veja feedbacks publicos e curta os que voce gostou.',
    category: 'monitoramento',
    categoryIcon: '📊',
    route: '/feedback-360',
    navigateTo: '/feedback-360',
    illustration: '💬',
    lookFor: 'Veja os feedbacks recebidos e o formulario para enviar novos',
    roles: ['operador'],
    tips: [
      { icon: '🎭', text: 'Feedbacks anonimos protegem a identidade do autor', type: 'info' },
      { icon: '❤️', text: 'Curta feedbacks para incentivar a cultura de reconhecimento', type: 'success' },
    ],
  },

  // ============================================================
  // DESEMPENHO group (admin, qualidade, supervisor + operador)
  // ============================================================
  {
    id: 'goals',
    title: 'Metas e Objetivos',
    description: 'Metas em 4 escopos: individual, equipe, setor e global. Categorias: score, auditorias, treinos, badges e falha grave.',
    category: 'desempenho',
    categoryIcon: '🏆',
    route: '/metas',
    navigateTo: '/metas',
    illustration: '🎯',
    lookFor: 'Veja as metas com barras de progresso e deadlines',
    roleTitles: {
      operador: 'Minhas Metas',
    },
    roleDescriptions: {
      operador: 'Suas metas individuais e do setor. Acompanhe o progresso, prazos e categorias. Metas globais se aplicam a toda a empresa.',
    },
    roleLookFor: {
      operador: 'Veja suas metas pessoais, do setor e globais com progresso',
    },
    tips: [
      { icon: '💡', text: 'Metas globais se aplicam a toda a empresa', type: 'info' },
      { icon: '⚠️', text: 'Meta de "falha grave" = manter ZERO falhas no periodo', type: 'warning' },
    ],
  },
  {
    id: 'ranking',
    title: 'Ranking e Competicao',
    description: 'Ranking geral com score combinado (auditorias + treinos). Filtre por setor, supervisor, monitor e tipo (humano vs IA).',
    category: 'desempenho',
    categoryIcon: '🏆',
    route: '/ranking',
    navigateTo: '/ranking',
    illustration: '🏅',
    lookFor: 'Veja a tabela de ranking com posicao, score e streak',
    roleTitles: {
      operador: 'Meu Ranking',
    },
    roleDescriptions: {
      operador: 'Veja sua posicao no ranking geral. Seu score e calculado a partir das suas auditorias e treinos. Acompanhe sua vizinhanca na classificacao.',
    },
    roleLookFor: {
      operador: 'Veja seu card de posicao, stats e vizinhanca no ranking',
    },
    tips: [
      { icon: '📊', text: 'Score combinado = media ponderada de auditorias e treinos', type: 'info' },
      { icon: '🔥', text: 'Streak = dias consecutivos com atividade', type: 'info' },
    ],
  },
  {
    id: 'leagues',
    title: 'Ligas e Temporadas',
    description: 'Participe de 4 ligas: Bronze, Prata, Ouro e Platina. Cada liga tem faixa de score, recompensas e temporadas.',
    category: 'desempenho',
    categoryIcon: '🏆',
    route: '/ligas',
    navigateTo: '/ligas',
    illustration: '🎖️',
    lookFor: 'Veja as ligas, participantes e temporadas',
    roleTitles: {
      operador: 'Minha Liga',
    },
    roleDescriptions: {
      operador: 'Veja sua liga atual, posicao, pontuacao e sequencia. Suba no ranking para ser promovido para a proxima liga!',
    },
    roleLookFor: {
      operador: 'Veja sua liga, posicao e classificacao entre os participantes',
    },
    tips: [
      { icon: '🥇', text: 'Bronze: 0-50 | Prata: 51-100 | Ouro: 101-150 | Platina: 151-200', type: 'info' },
      { icon: '🎁', text: 'Cada liga oferece pontos bonus e badges exclusivas', type: 'success' },
    ],
  },
  {
    id: 'certificates',
    title: 'Certificados',
    description: 'Conquiste certificados profissionais completando desafios: X auditorias com score Y+. Acompanhe progresso e exporte em PDF.',
    category: 'desempenho',
    categoryIcon: '🏆',
    route: '/certificados',
    navigateTo: '/certificados',
    illustration: '🎓',
    lookFor: 'Veja certificados concluidos e em andamento com barra de progresso',
    roleTitles: {
      operador: 'Meus Certificados',
    },
    roleDescriptions: {
      operador: 'Seus certificados profissionais. Complete desafios de auditorias com score minimo para conquistar e exportar em PDF.',
    },
    roleLookFor: {
      operador: 'Veja seus certificados conquistados e em progresso',
    },
    tips: [
      { icon: '📄', text: 'Certificados concluidos podem ser exportados em PDF', type: 'info' },
    ],
  },
  {
    id: 'operators',
    title: 'Gestao de Operadores',
    description: 'Lista de operadores com scores, supervisor, setor e badges. Clique para ver perfil detalhado com historico.',
    category: 'desempenho',
    categoryIcon: '🏆',
    route: '/operadores',
    navigateTo: '/operadores',
    illustration: '👥',
    lookFor: 'Veja a lista de operadores com metricas resumidas',
    roles: ['admin', 'qualidade', 'supervisor'],
  },
  {
    id: 'operator-comparison',
    title: 'Comparacao de Operadores',
    description: 'Compare 2+ operadores lado a lado: scores, auditorias, treinos, falhas graves, badges e evolucao temporal.',
    category: 'desempenho',
    categoryIcon: '🏆',
    route: '/comparacao-operadores',
    navigateTo: '/comparacao-operadores',
    illustration: '⚖️',
    lookFor: 'Selecione operadores para ver a comparacao lado a lado',
    roles: ['admin', 'qualidade', 'supervisor'],
    tips: [
      { icon: '💡', text: 'Selecione ate 4 operadores para comparacao simultanea', type: 'info' },
    ],
  },

  // ============================================================
  // GESTAO group (varies by role)
  // ============================================================
  {
    id: 'supervisor-dashboard',
    title: 'Dashboard do Supervisor',
    description: 'Visao agregada da equipe: media de score, operadores ativos, distribuicao de performance e alertas de falha grave.',
    category: 'gestao',
    categoryIcon: '⚙️',
    route: '/dashboard-supervisor',
    navigateTo: '/dashboard-supervisor',
    illustration: '👩‍💼',
    lookFor: 'Veja as metricas agregadas da equipe do supervisor',
    roles: ['superadmin', 'admin', 'supervisor'],
    tips: [
      { icon: '💡', text: 'Admin e SuperAdmin podem selecionar qual supervisor visualizar', type: 'info' },
    ],
  },
  {
    id: 'sector-dashboard',
    title: 'Dashboard por Setor',
    description: 'Compare desempenho entre setores: Suporte Tecnico, Vendas, Retencao e SAC. Metricas agregadas e top operadores.',
    category: 'gestao',
    categoryIcon: '⚙️',
    route: '/dashboard-setores',
    navigateTo: '/dashboard-setores',
    illustration: '🏢',
    lookFor: 'Veja a comparacao entre os 4 setores da empresa',
    roles: ['superadmin', 'admin', 'qualidade'],
  },
  {
    id: 'settings',
    title: 'Configuracoes',
    description: 'Personalize: perfil, seguranca, notificacoes e preferencias (tema, idioma, fuso horario). Escolha entre 10+ temas visuais.',
    category: 'gestao',
    categoryIcon: '⚙️',
    route: '/configuracoes',
    navigateTo: '/configuracoes',
    illustration: '⚙️',
    lookFor: 'Explore as abas de Perfil, Seguranca, Notificacoes e Preferencias',
    tips: [
      { icon: '🎨', text: 'Experimente o tema Dark para menor cansaco visual', type: 'info' },
      { icon: '🔄', text: 'Reinicie este tutorial em Preferencias → Reiniciar Tutorial', type: 'shortcut' },
    ],
  },

  // ============================================================
  // ADMIN group (superadmin, admin)
  // ============================================================
  {
    id: 'admin-users',
    title: 'Gestao de Usuarios',
    description: 'Gerencie operadores, supervisores, monitores e setores. Adicione, edite ou remova usuarios com vinculos hierarquicos corretos.',
    category: 'admin_tools',
    categoryIcon: '🛡️',
    route: '/admin/usuarios',
    navigateTo: '/admin/usuarios',
    illustration: '🛡️',
    lookFor: 'Veja as abas de operadores, supervisores e setores',
    roles: ['superadmin', 'admin'],
    tips: [
      { icon: '🔒', text: 'Apenas Admin e SuperAdmin podem gerenciar usuarios', type: 'warning' },
      { icon: '💡', text: 'Toda auditoria exige vinculo: operador → supervisor → setor', type: 'info' },
      { icon: '👥', text: 'Monitores sao usuarios com funcao de avaliacao (nao entidades)', type: 'info' },
    ],
  },
  {
    id: 'e2e-tests',
    title: 'Testes E2E',
    description: 'Suite de testes end-to-end da plataforma. Execute testes automatizados de integridade referencial, fluxos criticos e consistencia de dados.',
    category: 'admin_tools',
    categoryIcon: '🛡️',
    route: '/superadmin/testes-e2e',
    navigateTo: '/superadmin/testes-e2e',
    illustration: '🧪',
    lookFor: 'Veja os testes disponiveis e seus resultados',
    roles: ['superadmin'],
    tips: [
      { icon: '✅', text: 'Execute todos os testes para validar a integridade do sistema', type: 'success' },
      { icon: '⚠️', text: 'Testes com falha indicam problemas que precisam de correcao', type: 'warning' },
    ],
  },

  // ============================================================
  // CONCLUSION (all roles)
  // ============================================================
  {
    id: 'falha-grave',
    title: 'Regra: Falha Grave Zera',
    description: 'REGRA CRITICA: Se qualquer item "Falha Grave" no scorecard for reprovado, a nota INTEIRA fica ZERO. Itens: "Informacoes Incorretas" e "Tratamento Inadequado".',
    category: 'inicio',
    categoryIcon: '🏠',
    navigateTo: '/auditorias',
    illustration: '🚨',
    lookFor: 'Observe auditorias com score ZERO causadas por falha grave',
    roleDescriptions: {
      operador: 'REGRA CRITICA: Se voce cometer uma Falha Grave durante o atendimento, a nota da auditoria inteira fica ZERO. Evite: informacoes incorretas, grosseria e abandono de atendimento.',
      superadmin: 'REGRA CRITICA do sistema: Se qualquer item "Falha Grave" for reprovado, a nota da auditoria fica ZERO automaticamente. Isso se aplica a todas as empresas.',
    },
    roleNavigateTo: {
      superadmin: '/superadmin/dashboard',
    },
    tips: [
      { icon: '⚠️', text: 'Desinformacao (politicas erradas) = Nota ZERO', type: 'warning' },
      { icon: '⚠️', text: 'Grosseria ou desrespeito = Nota ZERO', type: 'warning' },
      { icon: '⚠️', text: 'Abandono de atendimento = Nota ZERO', type: 'warning' },
      { icon: '⚠️', text: 'Violacao de LGPD (dados de terceiros) = Nota ZERO', type: 'warning' },
    ],
  },
  {
    id: 'complete',
    title: 'Tutorial Completo!',
    description: 'Voce agora conhece todos os modulos do SimulaDesk. Comece pelo Dashboard, faca seu primeiro treino e acompanhe sua evolucao. Boa sorte!',
    category: 'inicio',
    categoryIcon: '🏠',
    navigateTo: '/',
    illustration: '🎉',
    roleDescriptions: {
      operador: 'Pronto! Voce conhece todas as telas disponiveis para voce. Comece treinando em um cenario facil, acompanhe seu ranking e conquiste certificados. Boa sorte!',
      supervisor: 'Pronto! Voce conhece todos os modulos de gestao. Comece pelo Dashboard do Supervisor para acompanhar sua equipe. Boa sorte!',
      superadmin: 'Pronto! Voce conhece as ferramentas de gestao multi-tenant. Acompanhe as empresas pela Visao Geral e gerencie usuarios e configuracoes. Boa sorte!',
    },
    roleNavigateTo: {
      superadmin: '/superadmin/dashboard',
    },
    tips: [
      { icon: '🚀', text: 'Sugestao: Comece com um treino facil para se familiarizar', type: 'success' },
      { icon: '📊', text: 'Depois, explore o Dashboard e acompanhe suas metricas', type: 'info' },
      { icon: '🔄', text: 'Rever tutorial: Configuracoes → Reiniciar Tutorial', type: 'shortcut' },
    ],
  },
];

// ========================
// TIP BADGE COMPONENT
// ========================

function TipBadge({ tip }: { tip: TutorialTip }) {
  const getBgColor = () => {
    switch (tip.type) {
      case 'warning': return 'var(--destructive-muted)';
      case 'success': return 'color-mix(in srgb, var(--success) 8%, transparent)';
      case 'shortcut': return 'color-mix(in srgb, var(--primary) 8%, transparent)';
      default: return 'var(--muted)';
    }
  };

  const getBorderColor = () => {
    switch (tip.type) {
      case 'warning': return 'var(--warning)';
      case 'success': return 'var(--success)';
      case 'shortcut': return 'var(--primary)';
      default: return 'var(--border)';
    }
  };

  return (
    <div
      className="flex items-start gap-2 px-3 py-2"
      style={{
        backgroundColor: getBgColor(),
        borderRadius: 'var(--radius)',
        borderLeft: `3px solid ${getBorderColor()}`,
        fontFamily: 'var(--font-family-inter)',
        fontSize: 'var(--text-xs)',
        color: 'var(--foreground)',
      }}
    >
      <span className="shrink-0" style={{ fontSize: 'var(--text-sm)' }}>{tip.icon}</span>
      <span style={{ lineHeight: '1.5' }}>{tip.text}</span>
    </div>
  );
}

// ========================
// CATEGORY PILL
// ========================

function CategoryPill({ category, isActive, onClick }: { category: { id: string; label: string; icon: string }; isActive: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      className="flex items-center gap-1.5 px-3 py-1.5 transition-all shrink-0"
      style={{
        backgroundColor: isActive ? 'var(--primary)' : 'var(--muted)',
        color: isActive ? 'var(--primary-foreground)' : 'var(--muted-foreground)',
        borderRadius: 'var(--radius-button)',
        border: 'none',
        cursor: 'pointer',
        fontFamily: 'var(--font-family-inter)',
        fontSize: 'var(--text-xs)',
        fontWeight: 'var(--font-weight-medium)',
        whiteSpace: 'nowrap',
      }}
    >
      <span>{category.icon}</span>
      <span>{category.label}</span>
    </button>
  );
}

// ========================
// NAVIGATION → TUTORIAL MAPPING HELPERS
// ========================

/** Maps a sidebar group label to a display icon for the tutorial pill */
function getCategoryIcon(label: string): string {
  const icons: Record<string, string> = {
    'SUPER ADMIN': '🌐',
    'PRINCIPAL': '🏠',
    'QUALIDADE': '🛡️',
    'MONITORAMENTO': '📊',
    'TREINAMENTO': '🎯',
    'DESEMPENHO': '🏆',
    'GESTAO': '⚙️',
    'ADMIN': '🛡️',
    // Operador-specific groups
    'MEU ESPACO': '🏠',
    'MINHAS AVALIACOES': '📊',
    'CONTA': '⚙️',
  };
  return icons[label] || '📋';
}

/**
 * Resolves which navigation category a tutorial step belongs to.
 * Uses the step's route to look up its sidebar group.
 * Falls back to '__intro__' for welcome/conclusion steps.
 */
function resolveStepCategory(
  step: OnboardingStep,
  _role: UserRole,
  navCats: { label: string; items: { href: string }[] }[]
): string {
  const targetRoute = step.route || step.navigateTo;
  if (!targetRoute) return '__intro__';

  for (const cat of navCats) {
    if (cat.items.some(item => item.href === targetRoute)) {
      return cat.label;
    }
  }
  // Fallback: the step references a route not in the sidebar (e.g. audit detail /auditorias/:id)
  // Try to match by prefix
  for (const cat of navCats) {
    if (cat.items.some(item => targetRoute.startsWith(item.href) && item.href !== '/')) {
      return cat.label;
    }
  }
  return '__intro__';
}

// ========================
// MAIN COMPONENT
// ========================

export function Onboarding() {
  const { user } = useAuth();
  const userRole = user?.role || 'operador';

  // Derive visible routes from the shared navigation config (single source of truth)
  const visibleRoutes = useMemo(() => getVisibleRoutes(userRole), [userRole]);

  // Filter steps by combining two checks:
  // 1. Route check — the step's route must be in the user's visible nav routes
  // 2. Role check — if step has an explicit `roles` array, the user must be in it
  //    (needed for duplicate-route steps like dashboard vs dashboard-operador)
  const steps = useMemo(() => {
    return allSteps.filter(step => {
      // If step has explicit roles, the current role must be in the list
      if (step.roles && !step.roles.includes(userRole)) return false;

      const targetRoute = step.route || step.navigateTo;
      // Steps without a route pass if roles check passed (welcome, conclusion, etc.)
      if (!targetRoute) return true;

      // Exact match against the navigation config
      if (visibleRoutes.has(targetRoute)) return true;
      // Prefix match for parameterized routes (e.g. /auditorias/audit-1 matches /auditorias)
      for (const route of visibleRoutes) {
        if (route !== '/' && targetRoute.startsWith(route + '/')) return true;
      }
      return false;
    });
  }, [userRole, visibleRoutes]);

  // Shared navigation categories for this role (computed once)
  const navCats = useMemo(() => getVisibleCategories(userRole), [userRole]);

  // Resolve a step's category from the navigation config
  const getResolvedCategory = useCallback(
    (step: OnboardingStep) => resolveStepCategory(step, userRole, navCats),
    [userRole, navCats]
  );

  // Build category pills dynamically from the navigation config
  const categories = useMemo(() => {
    const catPills = navCats.map(cat => ({
      id: cat.label,
      label: cat.label,
      icon: getCategoryIcon(cat.label),
    }));
    // Add a synthetic "inicio" pill for welcome/conclusion steps that always appear
    const hasInitSteps = steps.some(s => !s.route && !s.navigateTo);
    if (hasInitSteps && !catPills.find(c => c.id === 'INICIO')) {
      catPills.unshift({ id: '__intro__', label: 'Inicio', icon: '👋' });
    }
    // Only return categories that have at least one step
    const stepCategoryIds = new Set(steps.map(s => getResolvedCategory(s)));
    return catPills.filter(c => stepCategoryIds.has(c.id));
  }, [navCats, steps, getResolvedCategory]);

  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);
  const [showCategoryNav, setShowCategoryNav] = useState(false);
  const [direction, setDirection] = useState(1);
  const [isPeeking, setIsPeeking] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const previousRouteRef = useRef(location.pathname);

  // Resolve role-specific content
  const getStepTitle = useCallback((step: OnboardingStep) => {
    return step.roleTitles?.[userRole] || step.title;
  }, [userRole]);

  const getStepDescription = useCallback((step: OnboardingStep) => {
    return step.roleDescriptions?.[userRole] || step.description;
  }, [userRole]);

  const getStepNavigateTo = useCallback((step: OnboardingStep) => {
    return step.roleNavigateTo?.[userRole] || step.navigateTo;
  }, [userRole]);

  const getStepLookFor = useCallback((step: OnboardingStep) => {
    return step.roleLookFor?.[userRole] || step.lookFor;
  }, [userRole]);

  useEffect(() => {
    const storageKey = `simuladesk-onboarding-complete-${userRole}`;
    const hasSeenOnboarding = localStorage.getItem(storageKey);
    if (!hasSeenOnboarding) {
      previousRouteRef.current = location.pathname;
      setTimeout(() => setIsOpen(true), 1000);
    }
  }, [userRole]);

  // Listen for custom event to reopen tutorial
  useEffect(() => {
    const handleStartTutorial = () => {
      previousRouteRef.current = location.pathname;
      setCurrentStep(0);
      setDirection(1);
      setIsPeeking(false);
      setShowCategoryNav(false);
      setIsOpen(true);
    };
    window.addEventListener('simuladesk-start-tutorial', handleStartTutorial);
    return () => window.removeEventListener('simuladesk-start-tutorial', handleStartTutorial);
  }, [location.pathname]);

  // Navigate to step's route when step changes
  useEffect(() => {
    if (!isOpen || !steps[currentStep]) return;
    const step = steps[currentStep];
    const nav = getStepNavigateTo(step);
    if (nav) {
      navigate(nav, { replace: true });
    }
  }, [currentStep, isOpen, steps]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowRight' || e.key === 'Enter') {
        e.preventDefault();
        handleNext();
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevious();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleSkip();
      } else if (e.key === ' ') {
        e.preventDefault();
        setIsPeeking(prev => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, currentStep]);

  const handleNext = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setDirection(1);
      setCurrentStep(prev => prev + 1);
      setIsPeeking(false);
    } else {
      handleComplete();
    }
  }, [currentStep, steps.length]);

  const handlePrevious = useCallback(() => {
    if (currentStep > 0) {
      setDirection(-1);
      setCurrentStep(prev => prev - 1);
      setIsPeeking(false);
    }
  }, [currentStep]);

  const markOnboardingComplete = useCallback(() => {
    const storageKey = `simuladesk-onboarding-complete-${userRole}`;
    localStorage.setItem(storageKey, 'true');
    // Also persist to backend preferences (fire-and-forget)
    savePreferenceSettings({ language: 'pt-BR', timezone: 'America/Sao_Paulo', theme: 'light', onboardingComplete: true } as any).catch(() => {});
  }, [userRole]);

  const handleSkip = () => {
    setIsOpen(false);
    markOnboardingComplete();
    navigate(previousRouteRef.current, { replace: true });
  };

  const handleComplete = () => {
    setIsOpen(false);
    markOnboardingComplete();
    navigate('/', { replace: true });
  };

  const jumpToCategory = (categoryId: string) => {
    const idx = steps.findIndex(s => getResolvedCategory(s) === categoryId);
    if (idx >= 0) {
      setDirection(idx > currentStep ? 1 : -1);
      setCurrentStep(idx);
      setShowCategoryNav(false);
      setIsPeeking(false);
    }
  };

  if (!steps[currentStep]) return null;

  const step = steps[currentStep];
  const stepCategory = getResolvedCategory(step);
  const progress = ((currentStep + 1) / steps.length) * 100;
  const currentCategorySteps = steps.filter(s => getResolvedCategory(s) === stepCategory);
  const currentCategoryIndex = currentCategorySteps.findIndex(s => s.id === step.id) + 1;
  const currentCategoryLabel = categories.find(c => c.id === stepCategory)?.label || stepCategory;
  const stepLookFor = getStepLookFor(step);
  const stepNavigateTo = getStepNavigateTo(step);

  if (!isOpen) return null;

  return (
    <>
      {/* Overlay */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: isPeeking ? 0 : 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.3 }}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'linear-gradient(135deg, rgba(0,0,0,0.35) 0%, rgba(0,0,0,0.15) 50%, rgba(0,0,0,0.35) 100%)',
          zIndex: 9998,
          pointerEvents: isPeeking ? 'none' : 'auto',
        }}
        onClick={handleSkip}
      />

      {/* Floating Tutorial Panel */}
      <AnimatePresence>
        {!isPeeking && (
          <motion.div
            initial={{ opacity: 0, x: 60, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 60, scale: 0.95 }}
            transition={{ type: 'spring', damping: 24, stiffness: 300 }}
            style={{
              position: 'fixed',
              top: '16px',
              right: '16px',
              bottom: '16px',
              width: '420px',
              maxWidth: 'calc(100vw - 32px)',
              backgroundColor: 'var(--card)',
              borderRadius: 'var(--radius-card)',
              border: '1px solid var(--border)',
              boxShadow: '0 25px 65px rgba(0, 0, 0, 0.3)',
              zIndex: 9999,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              fontFamily: 'var(--font-family-inter)',
            }}
          >
            {/* Progress Bar */}
            <div style={{ height: '4px', backgroundColor: 'var(--muted)', flexShrink: 0 }}>
              <motion.div
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.4, ease: 'easeOut' }}
                style={{ height: '100%', backgroundColor: 'var(--primary)' }}
              />
            </div>

            {/* Header */}
            <div
              className="flex items-center justify-between px-5 py-3"
              style={{ borderBottom: '1px solid var(--border)', flexShrink: 0 }}
            >
              <div className="flex items-center gap-2.5 min-w-0">
                <div
                  className="flex items-center justify-center shrink-0"
                  style={{
                    width: '32px',
                    height: '32px',
                    borderRadius: 'var(--radius)',
                    backgroundColor: 'var(--primary)',
                    fontSize: 'var(--text-sm)',
                  }}
                >
                  {getCategoryIcon(stepCategory)}
                </div>
                <div className="min-w-0">
                  <h3
                    className="truncate"
                    style={{
                      fontFamily: 'var(--font-family-inter)',
                      fontSize: 'var(--text-base)',
                      fontWeight: 'var(--font-weight-semibold)',
                      color: 'var(--foreground)',
                      margin: 0,
                      lineHeight: '1.3',
                    }}
                  >
                    {getStepTitle(step)}
                  </h3>
                  <p
                    style={{
                      fontFamily: 'var(--font-family-inter)',
                      fontSize: 'var(--text-xs)',
                      color: 'var(--muted-foreground)',
                      margin: 0,
                    }}
                  >
                    {currentStep + 1}/{steps.length} &middot; {currentCategoryLabel} ({currentCategoryIndex}/{currentCategorySteps.length})
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button
                  onClick={() => setIsPeeking(true)}
                  className="transition-opacity hover:opacity-70"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--muted-foreground)',
                    padding: '4px',
                  }}
                  aria-label="Espiar a tela"
                  title="Espiar a tela (Espaco)"
                >
                  <Eye size={18} />
                </button>
                <button
                  onClick={handleSkip}
                  className="transition-opacity hover:opacity-70"
                  style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    color: 'var(--muted-foreground)',
                    padding: '4px',
                  }}
                  aria-label="Fechar tutorial"
                >
                  <X size={18} />
                </button>
              </div>
            </div>

            {/* Category Navigation (collapsible) */}
            <div style={{ flexShrink: 0 }}>
              <button
                onClick={() => setShowCategoryNav(!showCategoryNav)}
                className="w-full flex items-center justify-between px-5 py-1.5 transition-colors hover:opacity-80"
                style={{
                  backgroundColor: 'var(--muted)',
                  border: 'none',
                  cursor: 'pointer',
                  fontFamily: 'var(--font-family-inter)',
                  fontSize: 'var(--text-xs)',
                  color: 'var(--muted-foreground)',
                  fontWeight: 'var(--font-weight-medium)',
                }}
              >
                <span>Ir para categoria</span>
                {showCategoryNav ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              </button>
              <AnimatePresence>
                {showCategoryNav && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    className="overflow-hidden"
                  >
                    <div className="flex gap-1.5 px-5 py-2 flex-wrap" style={{ borderBottom: '1px solid var(--border)' }}>
                      {categories.map(cat => (
                        <CategoryPill
                          key={cat.id}
                          category={cat}
                          isActive={stepCategory === cat.id}
                          onClick={() => jumpToCategory(cat.id)}
                        />
                      ))}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Content - Scrollable */}
            <div className="overflow-y-auto flex-1 px-5 py-4">
              <AnimatePresence mode="wait">
                <motion.div
                  key={step.id}
                  initial={{ opacity: 0, x: direction * 30 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: direction * -30 }}
                  transition={{ duration: 0.2, ease: 'easeOut' }}
                >
                  {/* Illustration Circle */}
                  <div
                    className="mx-auto mb-4 flex items-center justify-center"
                    style={{
                      width: '56px',
                      height: '56px',
                      borderRadius: '50%',
                      backgroundColor: 'var(--primary)',
                      fontSize: 'var(--text-3xl)',
                    }}
                  >
                    {step.illustration}
                  </div>

                  {/* Description */}
                  <p
                    style={{
                      fontFamily: 'var(--font-family-inter)',
                      fontSize: 'var(--text-sm)',
                      fontWeight: 'var(--font-weight-normal)',
                      textAlign: 'center',
                      lineHeight: '1.7',
                      color: 'var(--foreground)',
                      margin: '0 0 12px 0',
                    }}
                  >
                    {getStepDescription(step)}
                  </p>

                  {/* "Look for" callout */}
                  {stepLookFor && (
                    <div
                      className="flex items-start gap-2 px-3 py-2.5 mb-3"
                      style={{
                        backgroundColor: 'color-mix(in srgb, var(--primary) 6%, transparent)',
                        borderRadius: 'var(--radius)',
                        border: '1px dashed var(--primary)',
                      }}
                    >
                      <MapPin size={14} className="shrink-0 mt-0.5" style={{ color: 'var(--primary)' }} />
                      <span
                        style={{
                          fontFamily: 'var(--font-family-inter)',
                          fontSize: 'var(--text-xs)',
                          color: 'var(--primary)',
                          fontWeight: 'var(--font-weight-medium)',
                          lineHeight: '1.5',
                        }}
                      >
                        {stepLookFor}
                      </span>
                    </div>
                  )}

                  {/* Active route badge */}
                  {stepNavigateTo && (
                    <div className="flex justify-center mb-3">
                      <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-1"
                        style={{
                          backgroundColor: 'var(--muted)',
                          borderRadius: 'var(--radius-button)',
                          fontFamily: 'var(--font-family-mono)',
                          fontSize: 'var(--text-xs)',
                          color: 'var(--muted-foreground)',
                        }}
                      >
                        <LayoutDashboard size={10} />
                        Tela atual: {stepNavigateTo}
                      </span>
                    </div>
                  )}

                  {/* Tips */}
                  {step.tips && step.tips.length > 0 && (
                    <div className="flex flex-col gap-1.5 mt-3">
                      {step.tips.map((tip, i) => (
                        <TipBadge key={i} tip={tip} />
                      ))}
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>

            {/* Peek hint */}
            <div
              className="flex items-center justify-center gap-1.5 py-1.5"
              style={{
                backgroundColor: 'var(--muted)',
                fontFamily: 'var(--font-family-inter)',
                fontSize: 'var(--text-xs)',
                color: 'var(--muted-foreground)',
                flexShrink: 0,
              }}
            >
              <Eye size={10} />
              <span>Pressione <strong>Espaco</strong> para espiar a tela</span>
            </div>

            {/* Footer */}
            <div
              className="flex items-center justify-between px-5 py-3"
              style={{ borderTop: '1px solid var(--border)', flexShrink: 0 }}
            >
              <button
                onClick={handleSkip}
                className="px-2 py-1 transition-opacity hover:opacity-70"
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--muted-foreground)',
                  fontFamily: 'var(--font-family-inter)',
                  fontSize: 'var(--text-xs)',
                  fontWeight: 'var(--font-weight-normal)',
                }}
              >
                Pular
              </button>

              <div className="flex items-center gap-2">
                {currentStep > 0 && (
                  <button
                    onClick={handlePrevious}
                    className="flex items-center gap-1 px-3 py-1.5 transition-opacity hover:opacity-90"
                    style={{
                      backgroundColor: 'var(--muted)',
                      color: 'var(--foreground)',
                      borderRadius: 'var(--radius-button)',
                      border: 'none',
                      cursor: 'pointer',
                      fontFamily: 'var(--font-family-inter)',
                      fontSize: 'var(--text-xs)',
                      fontWeight: 'var(--font-weight-medium)',
                    }}
                  >
                    <ArrowLeft size={14} />
                    Voltar
                  </button>
                )}

                <button
                  onClick={handleNext}
                  className="flex items-center gap-1 px-4 py-1.5 transition-opacity hover:opacity-90"
                  style={{
                    backgroundColor: currentStep === steps.length - 1 ? 'var(--success)' : 'var(--primary)',
                    color: currentStep === steps.length - 1 ? 'var(--success-foreground)' : 'var(--primary-foreground)',
                    borderRadius: 'var(--radius-button)',
                    border: 'none',
                    cursor: 'pointer',
                    fontFamily: 'var(--font-family-inter)',
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-weight-semibold)',
                  }}
                >
                  {currentStep === steps.length - 1 ? (
                    <>
                      <Check size={14} />
                      Concluir
                    </>
                  ) : (
                    <>
                      Proximo
                      <ArrowRight size={14} />
                    </>
                  )}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Peek mode */}
      <AnimatePresence>
        {isPeeking && (
          <motion.button
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 20 }}
            transition={{ duration: 0.2 }}
            onClick={() => setIsPeeking(false)}
            className="flex items-center gap-2 px-5 py-3 transition-opacity hover:opacity-90"
            style={{
              position: 'fixed',
              bottom: '24px',
              right: '24px',
              zIndex: 9999,
              backgroundColor: 'var(--primary)',
              color: 'var(--primary-foreground)',
              borderRadius: 'var(--radius-card)',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-inter)',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-semibold)',
              boxShadow: '0 8px 30px rgba(0, 0, 0, 0.25)',
            }}
          >
            <EyeOff size={16} />
            Voltar ao Tutorial ({currentStep + 1}/{steps.length})
          </motion.button>
        )}
      </AnimatePresence>
    </>
  );
}

// Exportar funcao para resetar o onboarding (usado em Settings)
export function resetOnboarding() {
  // Clear all role-specific keys
  const roles = ['superadmin', 'admin', 'qualidade', 'supervisor', 'operador'];
  roles.forEach(role => {
    localStorage.removeItem(`simuladesk-onboarding-complete-${role}`);
  });
  // Also clear legacy key
  localStorage.removeItem('simuladesk-onboarding-complete');
  window.location.reload();
}

/** Abre o tutorial sem recarregar a pagina */
export function startTutorial() {
  const roles = ['superadmin', 'admin', 'qualidade', 'supervisor', 'operador'];
  roles.forEach(role => {
    localStorage.removeItem(`simuladesk-onboarding-complete-${role}`);
  });
  localStorage.removeItem('simuladesk-onboarding-complete');
  window.dispatchEvent(new CustomEvent('simuladesk-start-tutorial'));
}